import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import editIcon from 'assets/svg/edit-icon.svg';
import { Text1Bold, Text2 } from 'components/typography/Texts';
import { useVerifyPhonePopup } from 'features/account/verify-phone/useVerifyPhonePopup';
import { useUpdateMyProfileDetails } from 'features/my-profile/account-settings/hooks/useUpdateMyProfileDetails';

import { ContentSection } from './ContentSection';

const Container = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${theme.color.borderGray};
  padding: 10px 0;
`;

const Label = styled(Text2)`
  width: 120px;
  ${theme.mq.tablet} {
    width: 80px;
  }
`;

const EditText = styled(Text1Bold)`
  color: ${theme.color.red};
`;

const EditContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
`;

type FieldType = 'firstName' | 'lastName' | 'about' | 'phone' | 'email';

interface UserDataInputProps {
  label: string;
  value: string;
  field: FieldType;
  onConfirmed?: () => void;
}

export const UserDataInput = ({ label, value, field, onConfirmed }: UserDataInputProps) => {
  const { t } = useTranslation('myProfile');
  const { updateMyProfileDetails, isLoading } = useUpdateMyProfileDetails();
  const [inputValue, setInputValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const { showVerifyPhonePopup } = useVerifyPhonePopup();

  const isEditable = field !== 'email';
  const hasChanges = inputValue !== value;

  const handleEditOrSaveClick = async () => {
    if (field === 'phone') {
      showVerifyPhonePopup(onConfirmed);
      return;
    }

    if (isEditing && hasChanges) {
      const payload = { [field]: inputValue };
      await updateMyProfileDetails(payload);
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (value: string) => {
    setInputValue(value);
  };

  return (
    <Container data-testid={`user-data-${field}`}>
      <Label>{label}</Label>
      <ContentSection
        isLoading={isLoading}
        isEditing={isEditing}
        inputValue={inputValue}
        onChange={handleChange}
        fieldType={field}
      />
      {isEditable && (
        <EditContainer data-testid={'edit-btn'} onClick={() => handleEditOrSaveClick()}>
          <img src={editIcon} alt={'edit icon'} />
          <EditText>{isEditing ? t('account-settings.save') : t('account-settings.edit')}</EditText>
        </EditContainer>
      )}
    </Container>
  );
};
